import type { GaApp } from '~/modules/app'

import type modules from '~/modules'

// Базовые типы для слоев модулей
export type TBaseAppModuleStores = (app: GaApp) => Record<string, unknown>
export type TBaseAppModuleServices = (app: GaApp) => Record<string, unknown>
export type TBaseAppModuleRepositories = (app: GaApp) => Record<string, unknown>
export type TBaseAppModuleAnalytics = (app: GaApp, libs: unknown) => unknown

export interface TBaseAppModuleRoutes {
  moduleType: string
  pageType: string
  path: string
  meta: Record<string, any>
  component: () => Promise<globalThis.Component>
}

// Продвинутые утилитарные типы для извлечения информации о модулях
type ExtractModuleName<T> = T extends { meta: { name: infer N } } ? N : never

type ExtractModuleLayer<T, K extends string> = T extends {
  layers: Record<string, any>
}
  ? T['layers'] extends Record<K, infer L>
    ? L extends (...args: any[]) => infer R
      ? R
      : never
    : never
  : never

// Проверка наличия слоя в модуле
type HasLayer<T, K extends string> = T extends {
  layers: Record<string, any>
}
  ? K extends keyof T['layers']
    ? true
    : false
  : false

// Динамический вывод типов для всех модулей
export type TAppModules = (typeof modules)[number]

// Продвинутые типы для полного динамического вывода слоев
export type TAppModulesServices = {
  [K in TAppModules as ExtractModuleName<K>]: HasLayer<
    K,
    'services'
  > extends true
    ? ExtractModuleLayer<K, 'services'>
    : never
} & Record<string, never>

export type TAppModulesStores = {
  [K in TAppModules as ExtractModuleName<K>]: HasLayer<K, 'stores'> extends true
    ? ExtractModuleLayer<K, 'stores'>
    : never
} & Record<string, never>

export type TAppModulesRepositories = {
  [K in TAppModules as ExtractModuleName<K>]: HasLayer<
    K,
    'repositories'
  > extends true
    ? ExtractModuleLayer<K, 'repositories'>
    : never
} & Record<string, never>

// Базовый интерфейс модуля
export interface IBaseAppModule {
  meta: {
    name: TAppModules['meta']['name']
    locales?: Record<string, any>
    routes?: TBaseAppModuleRoutes[]
  }
  components?: Record<string, Component>
  layers?: {
    stores?: TBaseAppModuleStores
    services?: TBaseAppModuleServices
    repositories?: TBaseAppModuleRepositories
    analytics?: TBaseAppModuleAnalytics
  }
}

// Утилитарные типы для работы со слоями
export type TModuleLayerKeys =
  | 'services'
  | 'stores'
  | 'repositories'
  | 'analytics'

export interface IRepository<T = Record<string, () => { url: string }>> {
  gaApp: GaApp
  endpoints: T
}

export abstract class BaseRepository<T = Record<string, () => { url: string }>>
  implements IRepository<T>
{
  gaApp: GaApp
  endpoints: T

  constructor(gaApp: GaApp, endpoints: T) {
    this.gaApp = gaApp
    this.endpoints = endpoints
  }
}

export interface IService {
  gaApp: GaApp
}

export abstract class BaseService implements IService {
  gaApp: GaApp

  constructor(gaApp: GaApp) {
    this.gaApp = gaApp
  }
}
